import * as config from './config';
import assets from './assets';
import CacheByArgumentStrings from './mem/cache-by-argument-strings';
import { slot2in, in2slot } from './measurement';
import { AXIS_X, dimensions, drawerBox, frameAndPanel, join, mirror, panel, shelfWire, slatwall, uvNormalize, uvTranspose, verticalBoringPanel, } from './IIID';
import { DiagridBoxMesh, DiagridFaceMesh, GridBoxMesh, GridFaceMesh } from './mw/cubbies';
import { ThreeJSMesh } from './mw/three-js-mesh';
import { Join, SetNormal, Translate } from './mw/ops';

import { Box2, Box3, BoxGeometry, BufferGeometry, ExtrudeGeometry, Matrix4, Mesh, Shape, Vector2, Vector3 } from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import ModelLoader from './model-loader';
import logging from './logging';
import { Axis, resize, setOrigin } from './geometry';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { lerp } from './math/lerp';
import traverse from "./control-flow/traverse-generator";
import { Join as IndexedJoin, Group as JoinGroup } from './imw/join';
import { QUARTER_TURN } from './math/constants';
import { Quad } from './imw/quad';
import { ReverseWinding } from './imw/reverse-winding';
import { RigidTransformMesh } from './imw/rigid-transform-mesh';
import { ScaleUV } from './imw/scale-uv';
import { SetNormal as IndexedSetNormal, NormalAttr } from './imw/set-normal';
import { SweptMesh } from './imw/swept-mesh';
import { TransposeUV } from './imw/transpose-uv';
import { VertexList } from './imw/vertex-list';
import { generateBufferGeometry } from './imw/generate-buffer-geometry';
import { generateEdgebandProfile } from './view3d/generate-edgeband-profile';
import { IndexedThreeJSMesh } from './imw/indexed-three-js-mesh';
import { EnsureUV } from './imw/ensure-uv';
import { IndexedMeshWriter } from './imw/indexed-mesh-writer';


const log = logging("ParametricCache");
const ml = ModelLoader.getInstance();

type GeometryLibrary = Record<string, BufferGeometry>;

// Some parametric parts make take a dictionary of string parameters, see
// ShelfWire, for example.
type Parameters = Record<string, string>;

/**
 * Creates a single group for the given buffer geometry, effectively making the
 * Mesh using this geometry require a material array instead of a single
 * material.
 */
const singleGroup = (geo: BufferGeometry) => {
    if(geo.index) {
        geo.addGroup(0, geo.index.count, 0);
    } else {
        const pos = geo.getAttribute('position');
        geo.addGroup(0, pos.count, 0);
    }
};

type ExtensionFn = (...args: unknown[]) => unknown;

/**
 * The ParametricCache ends up being the front-end for IIID.js. If you request
 * a piece of geometry in a specific style and dimensions, you'll get a
 * shared reference to any existing geometry with the same specifications.
 */
export class ParametricCache {

    /** Maps the extension name to its implementation */
    extensions = new Map<string, ExtensionFn>();

    /** Maps the extension name to the list of material slots supported by the geometry. */
    slots = new Map<string, string[]>();

    /** Caches geometry loaded from OBJ files in the /geometry directory */
    geocache: CacheByArgumentStrings< Promise<GeometryLibrary> >;

    /** Caches the output */
    results: CacheByArgumentStrings< Promise<BufferGeometry> >;

    loader: OBJLoader;
    baseUrl: string;

    constructor() {
        this.geocache = new CacheByArgumentStrings();
        this.results = new CacheByArgumentStrings();
        this.loader = new OBJLoader();
        this.baseUrl = config.geometry;
    }

    /**
     * Add a factory extension. The supplied function must be a full
     * fledged function, not a fat arrow function. The extension will be called
     * bound to the mesh factory instance so it can access the cache.
     */
    extend(name: string, slots: string[], f: ExtensionFn) {
        if(this.extensions.has(name)) {
            throw new Error(`The extend method cannot override the extension named "${name}". Use the override method if you really want to override the existing extension.`);
        }
        this.slots.set(name, slots);
        this.extensions.set(name, f);
    }

    /**
     * You should always include a catch block when calling await pc.make(...).
     */
    make(name: string, ...args: unknown[]): Promise<BufferGeometry> {
        let result = this.results.get(name, ...args);
        if( result == null ){
            var ext = this.extensions.get(name);
            if(ext == null){
                throw new Error(`This factory doesn't have an extension by the name "${name}"`);
            }
            result = ext.apply(this, args);

            // Giving the BufferGeometry a name is very helpful for debugging
            result.then((item: unknown) => {
                if(item instanceof BufferGeometry) {
                    item.name = name;
                } else if(Array.isArray(item)) {
                    for(const unit of item) {
                        if(unit instanceof BufferGeometry) {
                            unit.name = name;
                        }
                    }
                }
            });

            this.results.put(result, name, ...args);
        }
        return result;
    }

    private load(name: string) {
        const url = `${this.baseUrl}/${name}.obj`;

        const promise: Promise<GeometryLibrary> = new Promise(
            (resolve, reject) => {
                this.loader.load(url,
                    object => {
                        let geometry: GeometryLibrary = {};
                        object.name = name;
                        object.traverse(child => {
                            if(child instanceof Mesh) {
                                geometry[child.name] = child.geometry;
                                child.geometry.name = name;
                            }
                        });
                        resolve(geometry);
                    },
                    null, // no progress event updates, thus null
                    error => reject(error)
                );
            });

        this.geocache.put(promise, name);
        return promise;
    }

    // Returns an Object appropriate to pass as the geometry parameter to
    // IIID.js. 
    getgeo(name: string): Promise<GeometryLibrary> {
        let promise = this.geocache.get(name);
        if(promise == null) {
            promise = this.load(name);
        }
        return promise;
    }

    // This creates a new object with geometries from both the a and b names.
    // This helps with creating doors using the IIID.door() function, which
    // expects geometry for both the frame and panel in its geometry parameter.
    async union(a:string, b: string) {
        const 
            ag = await this.getgeo(a),
            bg = await this.getgeo(b);
        return {...ag, ...bg};
    }

}

const pc = new ParametricCache();

pc.extend('FrameAndPanel',
          ['frame', 'panel'],
          async function(frameStyle: string, panelStyle: string, width: number, height: number, grain: string)
{
    log.debug('ParametricCache::FrameAndPanel', ...arguments);
    let geometry: BufferGeometry;
    if(frameStyle)
        geometry = await this.union(frameStyle, panelStyle);
    else
        geometry = await this.getgeo(panelStyle);

    const [frameGeo, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        frameStyle,
        panelStyle);

    // Set the grain direction by transposing the UVs for horizontal grain
    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    return [frameGeo, panelGeo];
});

// This is a front facing panel like what appears on the front of a drawer,
// door or hook rack.
pc.extend('FrontPanel',
          ['face'],
          async function(panelStyle: string, width: number, height:number, grain: 'horizontal' | 'vertical')
{
    const geometry = await this.getgeo(panelStyle);

    const panelGeo = panel(geometry, new Vector2(width, height));

    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    return panelGeo;
});

// This is a panel with *depth* and height, i.e. an upright between sections
pc.extend('Panel',
          ['face'],
          async function(panelStyle: string, depth: number, height: number, grain: string)
{
    log.debug('ParametricCache::Panel', ...arguments);
    let geometry = await this.getgeo(panelStyle);
    var panelGeo = panel(geometry,
                    new Vector2(depth, height));

    if(grain == 'horizontal') {
        uvTranspose(panelGeo);
    }

    uvNormalize(panelGeo);
    return panelGeo;
});

pc.extend('DrawerBox',
          ['face'],
          async function(width: number, height: number, depth: number, front)
{
    log.debug('ParametricCache::DrawerBox', ...arguments);
    return drawerBox(new Vector3(width, height, depth), front);
});

pc.extend('ScoopFront',
          ['face'],
          async function(width: number, height: number)
{
    log.debug('ParametricCache::ScoopFront', ...arguments);
    let geometry = await this.getgeo('panel_scoop_3x0.75');
    const [_, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        null, // no frame
        'panel_scoop_3x0.75');
    uvNormalize(panelGeo);
    return [null, panelGeo];
});

pc.extend('FlatFront',
          ['face'],
          async function(width: number, height: number) {
    log.debug('ParametricCache::FlatFront', ...arguments);
    let geometry = await this.getgeo('panel_plain_0.75in');
    const [_, panelGeo] = frameAndPanel(
        geometry,
        new Vector2(width, height),
        null, // no frame
        'panel_plain_0.75in');
    uvNormalize(panelGeo);
    return [null, panelGeo];
});

pc.extend('FixedShelf',
          ['face', 'edge'],
          async function(width: number, thickness: number, depth: number)
{
    log.debug('ParametricCache::FixedShelf', ...arguments);
    const
        basis = await pc.make(
            'BasicEdgeBandedShelf',
            width, thickness, depth
        ),
        clone = basis.clone();
    clone.translate(0, 0.375 - thickness, 0);
    return clone;
});

// See SlantedShelfWithFence for another example implementation that parallels
// this one, but that implementation has more helpful comments.
pc.extend('FixedShelfWithPuckLight',
          ['face', 'edge', 'puck', 'light'],
          async function(width: number,
                         thickness: number,
                         depth: number,
                         params: Record<string, string>)
{
    log.debug('ParametricCache::FixedShelfWithPuckLight', ...arguments);

    const
        [shelf, shelfGroups] = basicShelf(width, thickness, depth),
        puckObj = await ml.get(
            assets.resolve(params.puckAssetId, 'LowResModel')),
        puckGeos: BufferGeometry[] = [],
        puckSlots: IndexedMeshWriter<{}>[] = [];

    puckObj.traverse(obj => {
        if(obj instanceof Mesh) {
            puckGeos.push(obj.geometry.clone());
        }
    });

    const
        count = Number(params.count ?? 1),
        z = depth / 2;
    // Each geometry from the loaded model represents a material slot of the
    // puck light, so we need to duplicate/transform these together so that
    // the final join preserves the material slot structure.
    for(const geo of puckGeos) {
        const
            x = width / (count + 1),
            instances: IndexedMeshWriter<{}>[] = [];
        for(let i=0; i < count; i ++) {
            const matrix = new Matrix4();
            matrix.setPosition(x * (i+1), -thickness+3/8, z);
            instances.push(
                new RigidTransformMesh(
                    matrix, new EnsureUV(
                        new IndexedThreeJSMesh(geo)) as any));
        }
        puckSlots.push(new IndexedJoin(...instances));
    }

    const matrix = new Matrix4();
    matrix.setPosition(0, 3/8 - thickness, 0);
    const
        join = new IndexedJoin(
            new RigidTransformMesh(matrix, new IndexedThreeJSMesh(shelf)),
            ...puckSlots),
        geo = generateBufferGeometry(join);

    for(const group of shelfGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex);
    }

    const remainingGroups = join.groups.slice(1);
    for(const group of remainingGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex + shelfGroups.length - 1);
    }

    geo.name = 'FixedShelfWithPuckLight';

    return geo;
});

pc.extend('FixedShelfWithStripLight',
          ['face', 'edge', 'light'],
          async function(width: number,
                         thickness: number,
                         depth: number,
                         params: Record<string, string>)
{
    log.debug('ParametricCache::FixedShelfWithStripLight', ...arguments);

    const
        [shelf, shelfGroups] = basicShelf(width, thickness, depth),
        stripObj = await ml.get(
            assets.resolve(params.stripAssetId, 'LowResModel')),
        stripGeos: BufferGeometry[] = [],
        stripSlots: IndexedMeshWriter<{}>[] = [];

    stripObj.traverse(obj => {
        if(obj instanceof Mesh) {
            stripGeos.push(obj.geometry.clone());
        }
    });

    const z = depth - 2;
    // Each geometry from the loaded model represents a material slot of the
    // puck light, so we need to duplicate/transform these together so that
    // the final join preserves the material slot structure.
    for(const geo of stripGeos) {
        const matrix = new Matrix4();
        matrix.makeScale(width, 1, 1);
        matrix.setPosition(0, -thickness + 3/8, z);
        stripSlots.push(
            new RigidTransformMesh(
                matrix, new EnsureUV(
                    new IndexedThreeJSMesh(geo)) as any));
    }

    const matrix = new Matrix4();
    matrix.setPosition(0, 3/8 - thickness, 0);
    const
        join = new IndexedJoin(
            new RigidTransformMesh(matrix, new IndexedThreeJSMesh(shelf)),
            ...stripSlots),
        geo = generateBufferGeometry(join);

    for(const group of shelfGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex);
    }

    const remainingGroups = join.groups.slice(1);
    for(const group of remainingGroups) {
        geo.addGroup(group.start, group.count, group.materialIndex + shelfGroups.length - 1);
    }

    geo.name = 'FixedShelfWithStripLight';

    return geo;
});

pc.extend('VerticalBoringPanel',
          ['face'],
          async function(
              style: string,
              height: number,
              depth: number,
              mirrorPanel: boolean)
{
    log.debug('ParametricCache::VerticalBoringPanel', ...arguments);
    let geometry = await this.getgeo(style);
    let bgeo = verticalBoringPanel(
        geometry,
        new Vector2(depth, height),
        new Box2(
            new Vector2(2, slot2in(1)),
            new Vector2(depth-2, slot2in(in2slot(height)-1))
        )
    );
    // The result is laid out in the XY plane, but we want it in the XZ plane
    bgeo.rotateY(Math.PI/2);
    bgeo.translate(0, 0, depth);

    if(mirrorPanel) {
        bgeo = mirror(bgeo, AXIS_X);
    }

    return bgeo;
});

pc.extend('VerticalDivider',
          ['face', 'edge'],
          async function(thickness: number, height: number, depth: number)
{
    log.debug('ParametricCache::Divider', ...arguments);
    const
        basis = await pc.make(
            'BasicEdgeBandedShelf',
            height, thickness, depth,
        ),
        clone = basis.clone();
    clone.rotateZ(Math.PI/2);
    clone.translate(0.75, 0, 0);
    return clone;
});

pc.extend('AdjustableShelf',
          ['face', 'edge'],
          async function(width: number, thickness: number, depth: number)
{
    log.debug('ParametricCache::AdjustableShelf', ...arguments);
    const
        basis = await pc.make(
            'BasicEdgeBandedShelf',
            width, thickness, depth
        ),
        clone = basis.clone();
    return clone;
});

pc.extend('FlatShoeShelf',
          ['face', 'edge'],
          async function(width: number, height: number, depth: number)
{
    log.debug('ParametricCache::FlatShoeShelf', ...arguments);
    const shelf = await this.make('AdjustableShelf', width, height, depth);
    return shelf;
});

pc.extend('FixedShoeShelf',
          ['face', 'edge'],
          async function(width: number, height: number, depth: number)
{
    log.debug('ParametricCache::FixedShoeShelf', ...arguments);
    const shelf = await this.make('FixedShelf', width, height, depth);
    return shelf;
});


pc.extend('SlantedShelfWithFence',
          ['face', 'edge', 'fence'],
          async function(
              width: number,
              thickness: number,
              depth: number,
              params: Record<string, string>)
{
    try {
        const
            shelfAngle = Math.PI/180 * 15,
            sineShelfAngle = Math.sin(shelfAngle),
            rakeHeight = sineShelfAngle * depth,
            fenceInset = 0.5, // inches
            shelfDepth = Math.sqrt(depth*depth + rakeHeight*rakeHeight) - 1.0,
            [shelf, shelfGroups] = basicShelf(width, thickness, shelfDepth);

        shelf.rotateX(shelfAngle);
        shelf.translate(0, rakeHeight, 0.5);

        if(params.fenceAssetId) {
            const
                fence = await ml.get(assets.resolve(params.fenceAssetId, 'LowResModel')),
                fenceGeos: BufferGeometry[] = [];

            fence.traverse(obj => {
                if(obj instanceof Mesh) {
                    fenceGeos.push(obj.geometry.clone());
                }
            });

            for(const fenceGeo of fenceGeos) {
                fenceGeo.rotateX(shelfAngle);
                fenceGeo.translate(
                    0,
                    thickness + sineShelfAngle * fenceInset,
                    depth - fenceInset);
                fenceGeo.scale(width/23, 1, 1);
            }

            const
                join = new IndexedJoin(
                    new IndexedThreeJSMesh(shelf),
                    ...fenceGeos.map(g => new EnsureUV(new IndexedThreeJSMesh(g)))
                ),
                geo = generateBufferGeometry(join);

            // Add the groups for the face and edge banding from the original
            // shelf. The join operation has discarded these two groups, so we
            // have to restore them.
            for(const group of shelfGroups) {
                geo.addGroup(group.start, group.count, group.materialIndex);
            }
            // Slice off the first group from the join which is actually both
            // the face and edge banding, then add the remaining groups from
            // the join, which should belong to the fence model. Note that the
            // TAG Curate fence model ideally ought to have multiple material
            // slots, but may not yet be implemented.
            const remainingGroups = join.groups.slice(1);
            for(const group of remainingGroups) {
                geo.addGroup(group.start, group.count, group.materialIndex + shelfGroups.length - 1);
            }

            return geo;
        }
        else {
            // Add the main material and edge banding
            for(const group of shelfGroups) {
                shelf.addGroup(group.start, group.count, group.materialIndex);
            }
            return shelf;
        }
    } catch(error) {
        log.error(
            'SlantedShelfWithFence encountered an exception. Returning a blank geometry.',
            error);
        return new BufferGeometry();
    }
});

pc.extend('SlidingShelf',
          ['face', 'edge', 'slides'],
          async function(width: number, thickness: number, depth: number)
{
    log.debug('ParametricCache::SlidingShelf', ...arguments);
    const
        basis = await this.make('FixedShelf', width - 0.5, thickness, depth),
        clone = basis.clone();
    clone.translate(0.25, 0, 0);

    // The slide is a little smaller than the space available (by 0.2 in) so
    // that there is some recess to give it a readable silhouette against the
    // shelving material
    let slideLeft = new BoxGeometry(0.25, 0.55, depth - 0.2);
    // move the origin to the back left center
    slideLeft.translate(0.125, 0, depth/2);
    const slideRight = slideLeft.clone();
    slideRight.translate(width - 0.25, 0, 0);
    const slides = mergeGeometries([slideLeft, slideRight]);
    singleGroup(slides);
    slides.groups[0].materialIndex = 2;
    const joined = mergeGeometries([clone, slides]);

    joined.groups = clone.groups;
    const last = joined.groups[joined.groups.length - 1];
    joined.addGroup(
        last.start+last.count,
        slides.attributes.position.count * 2,
        2);

    return joined;
});

pc.extend('HangBar',
          ['bar'],
          async function(filename: string, width: number) {
    log.debug('ParametricCache::HangBar', ...arguments);
    const scene = await ml.get(`${config.models}/${filename}`);
    let geo: BufferGeometry;
    for(const obj of traverse(scene)) {
        if(obj instanceof Mesh) {
            geo = obj.geometry.clone();
        }
    }
    if(geo == null) {
        log.error(`Unable to load mesh from "${filename}"`);
        return;
    }

    if(geo.boundingBox == null) geo.computeBoundingBox();
    const dims = dimensions(geo);
    const scale = width / dims.x;
    geo.scale(scale, 1, 1);
    setOrigin(geo, 0, 0.5, 0.5);
    singleGroup(geo);
    return geo;
});


pc.extend('CubbyBoxes',
          ['face'],
          async function(
    countX: number, countY: number, diagonal: boolean, width: number, height: number, depth: number, thickness: number)
{
    let boxes: BufferGeometry;
    if(diagonal) {
        const uWidth = width / countX;
        let uHeight = height / countY;

        let unitB = new DiagridBoxMesh(
            uWidth, uHeight, depth, thickness);

        let bs = [];
        for(let x = 0; x < countX; x++) {
            for(let y = 0; y < countY; y++) {
                bs.push(new Translate(uWidth*x, uHeight*y, 0, unitB));
            }
        }
        boxes = ThreeJSMesh.toBufferGeometry((new Join(bs)).generate());
    } else {
        const boxMesh = new GridBoxMesh(width, height, depth,
            thickness, countX, countY);
        boxes = ThreeJSMesh.toBufferGeometry(boxMesh.generate());
    }

    return boxes;
});

pc.extend('CubbyFace',
          ['edge'],
          async function(countX: number, countY: number, diagonal: boolean, width: number, height: number, depth: number, thickness: number)
{
    let face: BufferGeometry;
    if(diagonal) {
        const uWidth = width / countX;
        let uHeight = height / countY;
        const unitMesh = new DiagridFaceMesh(uWidth, uHeight, thickness);
        const unitF = new SetNormal(0, 0, 1, unitMesh);

        let fs = [];
        for(let x = 0; x < countX; x++) {
            for(let y = 0; y < countY; y++) {
                fs.push(new Translate(uWidth*x, uHeight*y, depth, unitF));
            }
        }
        face = ThreeJSMesh.toBufferGeometry((new Join(fs)).generate());
    } else {
        const faceMesh = new SetNormal(0, 0, 1,
            new Translate(0, 0, depth,
                new GridFaceMesh(width, height, thickness,
                    countX, countY)));
        face = ThreeJSMesh.toBufferGeometry(faceMesh.generate());
    }

    return face;
});

pc.extend('ToeKick',
          ['face'],
          async function(width: number, height: number, depth: number)
{
    const
        basis = await pc.make(
            'BasicShelf',
            width, 0.75, height,
        ),
        clone = basis.clone();
    clone.rotateX(-QUARTER_TURN);
    clone.translate(0, 0, depth - 3);
    return clone;
});

const basicShelf = (width: number, thickness: number, depth: number): [BufferGeometry, JoinGroup[]] => {
    const
        // The edge banding is 1/16th of an inch thick
        offset = 0.0625,
        offset2 = 2*offset,
        w = width - offset2,
        d = depth - offset2,
        uvScale = Math.min(1/depth, 1/width),

        moveBottomQuad = new Matrix4(),
        bottom = new IndexedSetNormal(0, -1, 0, new RigidTransformMesh<NormalAttr>(
            moveBottomQuad, new ReverseWinding(new Quad(offset, offset, w, d)))),
        moveTopQuad = new Matrix4(),
        top = new IndexedSetNormal(0, 1, 0, new RigidTransformMesh<NormalAttr>(
            moveTopQuad, new ReverseWinding(bottom))),

        plan = new VertexList(),
        profile = new VertexList(),
        banding = new ScaleUV(
            uvScale, uvScale,
            new SweptMesh(profile, plan)),
        topAndBottom = new TransposeUV(
            new ScaleUV(
                uvScale, uvScale,
                new IndexedJoin(bottom, top) as any)),

        join = new IndexedJoin(topAndBottom, banding);

    moveTopQuad.makeTranslation(0, thickness, 0);
    moveBottomQuad.makeRotationX(-QUARTER_TURN);

    generateEdgebandProfile(profile, thickness, offset);
    plan.rect(offset, offset, w, d);

    const geo = generateBufferGeometry(join);

    geo.translate(0, 0, depth);

    return [geo, join.groups]
};

// The width, thickness, and depth correspond to the X, Y, and Z axes in
// three.js. The origin of this shelf is the back-bottom-left of the bounding
// box.
pc.extend('BasicEdgeBandedShelf',
          ['face', 'edge'],
          async function(width: number, thickness: number, depth: number)
{
    const [geo, groups] = basicShelf(width, thickness, depth);

    for(const group of groups) {
        geo.addGroup(group.start, group.count, group.materialIndex);
    }

    return geo;
});

// The BasicShelf is identical to the BasicEdgeBandedShelf except that it only
// has a single material slot.
pc.extend('BasicShelf',
          ['face'],
          async function(width: number, thickness: number, depth: number)
{
    const [geo, _] = basicShelf(width, thickness, depth);
    singleGroup(geo);

    return geo;
});

pc.extend('GlassShelf', ['face'], pc.extensions.get('BasicShelf'));

// Width, height, and thickness correspond to X, Y, and Z axes in three.js.
// The origin of this cleat is the back-bottom-left of the bounding box.
pc.extend('Cleat',
          ['face', 'edge'],
          async function(width: number, height: number, thickness: number)
{
    // The shelf is in the XZ plane with its thickness in Y, so our XYZ is
    // gets swizzled to XZY for the equivalent shelf
    const shelf = await this.make('BasicEdgeBandedShelf', width, thickness, height);

    // Now we only have to flip it up and pull it forward by the thickness
    // to achieve the cleat with its origin at the back-bottom-left.
    shelf.rotateX(-QUARTER_TURN);
    shelf.translate(0, 0, thickness);

    return shelf;
});

/**
 * Note that this will return an indexed mesh, so it is unlikely to work
 * directly with the IIID functions, which all expect triangle meshes.
 */
pc.extend('Resizable',
          [], // depends on the model we load, so unknowable in advance
          async function(
              url: string,
              width: number = -1, height: number = -1, depth: number = -1,
              ox: number = 0, oy: number = 0, oz: number = 0
          )
{
    const
        scene = (await ml.get(url)),
        geometries: BufferGeometry[] = [],
        bb = new Box3();

    scene.traverse(obj => {
        if((obj as any).isMesh) {
            const geo = (obj as Mesh).geometry.clone();
            geo.name = url;
            geo.computeBoundingBox();
            bb.expandByPoint(geo.boundingBox.min);
            bb.expandByPoint(geo.boundingBox.max);
            geometries.push(geo);
        }
    });

    let dx = 0, dy = 0, dz = 0;
    if(width != -1) {
        dx = width - (bb.max.x - bb.min.x);
    }
    if(height != -1) {
        dy = height - (bb.max.y - bb.min.y);
    }
    if(depth != -1) {
        dz = depth - (bb.max.z - bb.min.z);
    }

    for(const geo of geometries) {
        if(dx != 0) {
            resize(geo, dx, Axis.x);
        }
        if(dy != 0) {
            resize(geo, dy, Axis.y);
        }
        if(dz != 0) {
            resize(geo, dz, Axis.z);
        }
    }
    
    const result = mergeGeometries(geometries, true);
    setOrigin(result, ox, oy, oz);
    // vertex colors are used for resize, not needed for display, and will
    // prevent merging with other geometries
    result.deleteAttribute('color'); 

    return result;
});

type Space = {
    start: number,
    width: number,
    open: boolean,
}

const resample = (heights: number[], x: number) => {
    if(heights.length < 2) {
        log.error('ParametricCache resample() received invalid heights');
        return 0;
    }
    for(let i=0; i+3 < heights.length; i += 2) {
        const
            ax = heights[i],
            ah = heights[i+1],
            bx = heights[i+2],
            bh = heights[i+3];
        if(x >= ax && x <= bx) {
            const t = (x - ax) / (bx - ax);
            return lerp(ah, bh, isNaN(t) ? 0 : t);
        }
        //else if(x > bx) {
        //    return bh;
        //}
    }
    if(heights.length >= 2 && x <= heights[1]) {
        return heights[1];
    }
}

// To support the simple caching mechanism, we expect a flattened array of
// (x, height) pairs, i.e. the array should be interleaved so it looks like
// [x1, height1, ... xN, heightN].
pc.extend('Wall',
          ['face'],
          async function(heights: number[], spaces: Space[])
{
    const geos: BufferGeometry[] = [];
    for(const space of spaces) {
        const
            shape = new Shape(),
            start = Math.max(space.start, heights[0]),
            end = space.start + space.width;
        shape.moveTo(start, 0);
        shape.lineTo(start, resample(heights, start));
        for(let i = 0; i < heights.length; i += 2) {
            const x = heights[i];
            if(x < space.start) continue;
            if(x > end) break;

            const height = heights[i+1];
            shape.lineTo(x, height);
        }
        shape.lineTo(end, resample(heights, end));
        shape.lineTo(end, 0);

        const geo = new ExtrudeGeometry(shape, {
            depth: 4.5,
            bevelEnabled: false,
        });
        geo.translate(0, 0, -4.5);

        geos.push(geo);
    }
    return geos;
});

// The origin of the resulting object should be placed where the two walls meet,
// i.e. at the position of the second wall in the pair.
pc.extend('WallCornerFill',
          ['face'],
          async function(
              height: number,
              ax: number, ay: number,
              bx: number, by: number,
              cx: number, cy: number,
              )
{
    const shape = new Shape();
    shape.moveTo(0, 0);
    shape.lineTo(ax, ay);
    // c stands for 'corner', which is why it's in the middle
    shape.lineTo(cx, cy);
    shape.lineTo(bx, by);

    const geo = new ExtrudeGeometry(shape, {
        depth: height,
        bevelEnabled: false,
        });
    geo.rotateX(-Math.PI/2);
    return geo;
});

pc.extend('Slatwall',
          ['face', 'edge'],
          async function(width: number, height: number, _depth: number)
{
    log.debug('ParametricCache::Slatwall', ...arguments);
    const
        geometry = await this.getgeo('slatwall'),
        [facing, substrate] = slatwall(geometry,
                        new Vector2(width, height)),
        fpa = facing.getAttribute('position'),
        spa = substrate.getAttribute('position'),
        result: BufferGeometry = join([facing, substrate]);
        result.addGroup(0, fpa.count, 0);
        result.addGroup(fpa.count, spa.count, 1);

    return result;
})

pc.extend('ShelfWire',
          ['face'],
          async function(width: number, _height: number, _depth,
                                      parameters: Parameters)
{
    const
        geometry = await this.getgeo('shelf_wire'),
        geo: BufferGeometry = shelfWire(
            geometry,
            parameters["SKU"],
            new Vector2(width, 0));

    singleGroup(geo);
    return geo;
})

export default pc;
